import { css } from "styled-system/css";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { MapPin } from "lucide-react";
import { getAmenityIcon, type AmenityItem } from "@/lib/amenities-catalog";
import { useGoogleMapsLoader } from "@/hooks/use-google-maps-loader";
import { normalizePrimary } from "@/components/book/book-public-layout";

type Props = {
  address: string | null;
  lat: number | null;
  lng: number | null;
  amenities: AmenityItem[];
  primaryColor: string;
};

const MAP_CONTAINER_STYLE = { width: "100%", height: "260px" };

function directionsUrl(address: string | null, lat: number | null, lng: number | null): string {
  const destination = lat != null && lng != null ? `${lat},${lng}` : (address ?? "");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

export function BookInfoTab({ address, lat, lng, amenities, primaryColor }: Props) {
  const { isLoaded, apiKeyConfigured } = useGoogleMapsLoader();
  const accent = normalizePrimary(primaryColor);
  const hasPin = lat != null && lng != null;
  const hasAddress = Boolean(address?.trim());
  const hasAmenities = amenities.length > 0;

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
      {hasAddress || hasPin ? (
        <div>
          {apiKeyConfigured && isLoaded && hasPin ? (
            <div
              className={css({
                rounded: "xl",
                overflow: "hidden",
                borderWidth: "1px",
                borderColor: "var(--pp-border)",
                mb: "3",
              })}
            >
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={{ lat, lng }}
                zoom={16}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  gestureHandling: "cooperative",
                }}
              >
                <MarkerF position={{ lat, lng }} />
              </GoogleMap>
            </div>
          ) : null}

          <div className={css({ display: "flex", alignItems: "flex-start", gap: "3" })}>
            <MapPin size={18} style={{ color: accent, flexShrink: 0, marginTop: "2px" }} aria-hidden />
            <div className={css({ minW: 0 })}>
              {hasAddress ? (
                <p className={css({ fontSize: "sm", color: "var(--pp-text)", lineHeight: "1.5" })}>{address}</p>
              ) : null}
              <a
                href={directionsUrl(address, lat, lng)}
                target="_blank"
                rel="noopener noreferrer"
                className={css({ fontSize: "sm", fontWeight: "semibold", textDecoration: "underline", mt: "1", display: "inline-block" })}
                style={{ color: accent }}
              >
                Cómo llegar
              </a>
            </div>
          </div>
        </div>
      ) : null}

      {hasAmenities ? (
        <div>
          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "var(--pp-text)", mb: "3" })}>Comodidades</p>
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: "3",
            })}
          >
            {amenities.map((a) => {
              const Icon = getAmenityIcon(a.icono);
              return (
                <div
                  key={a.id}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "2",
                    fontSize: "sm",
                    color: "var(--pp-text)",
                    px: "3",
                    py: "2",
                    rounded: "lg",
                    bg: "var(--pp-card-bg)",
                    borderWidth: "1px",
                    borderColor: "var(--pp-border)",
                  })}
                >
                  <Icon size={16} style={{ color: "var(--pp-muted)", flexShrink: 0 }} aria-hidden />
                  <span>{a.nombre}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {!hasAddress && !hasPin && !hasAmenities ? (
        <p className={css({ fontSize: "sm", color: "var(--pp-muted)" })}>Todavía no hay información cargada.</p>
      ) : null}
    </div>
  );
}
